import {
  AppShellContent,
  Button,
  Group,
  Stack,
  Text,
  Title,
} from "@assetwatch/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  MotionAmplificationJob,
  MotionAmplificationJobId,
} from "../clients/motion-amplification";
import { MotionAmplificationDetailsModal } from "../components/motion-amplification/MotionAmplificationDetailsModal";
import { MotionAmplificationUploadModal } from "../components/motion-amplification/MotionAmplificationUploadModal";
import type { JobView } from "../components/motion-amplification/types";
import { MotionAmplificationGalleryAdapter } from "../components/snowflakes/MotionAmplificationGalleryAdapter";
import type { ActiveMotionAmplificationUpload } from "../mutations/useUploadMotionAmplificationVideoMutation";
import { useUploadMotionAmplificationVideoMutation } from "../mutations/useUploadMotionAmplificationVideoMutation";
import { useMotionAmplificationRawVideosQuery } from "../queries/useMotionAmplificationRawVideosQuery";

type MotionAmplificationJobsQueryState = {
  data: MotionAmplificationJob[] | undefined;
  error: Error | null;
  isLoading: boolean;
};

function defaultUploadState(
  job: MotionAmplificationJob,
): JobView["uploadState"] {
  if (job.status === "Completed") return "completed";
  if (job.status === "Failed") return "failed";
  return "processing";
}

/**
 * The server has no concept of "uploading"/"starting" - those only exist
 * while the client is PUTting the file to S3 and waiting for the job to
 * start. `activeUpload` overlays that client-only progress onto whichever
 * job it belongs to; every other job's view is derived purely from its
 * server status.
 */
function toJobView(
  job: MotionAmplificationJob,
  activeUpload: ActiveMotionAmplificationUpload | null,
): JobView {
  if (activeUpload?.jobId === job.jobId) {
    return {
      job,
      uploadProgress: activeUpload.progress,
      uploadState: activeUpload.progress < 100 ? "uploading" : "starting",
    };
  }
  return {
    job,
    uploadProgress:
      job.status === "Completed" ? 100 : (job.progressPercent ?? 0),
    uploadState: defaultUploadState(job),
  };
}

export function MotionAmplificationScreen({
  jobsQuery,
}: {
  jobsQuery: MotionAmplificationJobsQueryState;
}) {
  const { t } = useTranslation();
  const rawVideosQuery = useMotionAmplificationRawVideosQuery();
  const { uploadVideo, activeUpload } =
    useUploadMotionAmplificationVideoMutation();
  const jobs = (jobsQuery.data ?? [])
    .map((job) => toJobView(job, activeUpload))
    .sort((a, b) =>
      (b.job.dateCreated || "").localeCompare(a.job.dateCreated || ""),
    );
  const [selectedJobId, setSelectedJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailsJobId, setDetailsJobId] =
    useState<MotionAmplificationJobId | null>(null);
  const detailsJob =
    jobs.find(({ job }) => job.jobId === detailsJobId)?.job ?? null;

  async function handleUpload(submission: Parameters<typeof uploadVideo>[0]) {
    const jobId = await uploadVideo(submission);
    setSelectedJobId(jobId);
    setUploadOpen(false);
  }

  return (
    <AppShellContent
      pageHeader={
        <Group justify="between" align="center">
          <Title size="lg">{t("motionAmplification.pageTitle")}</Title>
          <Button icon="add-plus" onClick={() => setUploadOpen(true)}>
            {t("motionAmplification.addVideo")}
          </Button>
        </Group>
      }
    >
      <Stack gap={24}>
        {jobsQuery.error && (
          <Text tone="negative">
            {jobsQuery.error.message || t("motionAmplification.loadJobsError")}
          </Text>
        )}
        <MotionAmplificationGalleryAdapter
          jobs={jobs}
          isLoading={jobsQuery.isLoading}
          selectedJobId={selectedJobId}
          onSelectJob={setSelectedJobId}
          onShowDetails={(job) => setDetailsJobId(job.jobId)}
          onAddVideo={() => setUploadOpen(true)}
        />
        <MotionAmplificationUploadModal
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onSubmit={handleUpload}
          rawVideos={rawVideosQuery.data ?? []}
          isLoadingRawVideos={rawVideosQuery.isLoading}
        />
        <MotionAmplificationDetailsModal
          job={detailsJob}
          open={detailsJobId !== null}
          onOpenChange={(open) => {
            if (!open) setDetailsJobId(null);
          }}
        />
      </Stack>
    </AppShellContent>
  );
}
